import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  ApiBlueprint,
  createApiRef,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';

export interface Project {
  projectCode: string;
  templateName: string;
  environments: { name: 'dev' | 'prod'; status: 'deployed' | 'failed' }[];
  createdBy?: string;
  createdAt?: string;
}

export interface ProjectsApi {
  listProjects(clientCode?: string): Promise<Project[]>;
}

export const projectsApiRef = createApiRef<ProjectsApi>().with({
  id: 'plugin.scaffolder.project-picker.api',
});

export class ProjectsClient implements ProjectsApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  async listProjects(clientCode?: string): Promise<Project[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('scaffolder');
    const query = clientCode
      ? `?clientCode=${encodeURIComponent(clientCode)}`
      : '';
    const response = await this.fetchApi.fetch(`${baseUrl}/projects${query}`);

    if (!response.ok) {
      const body = await response.text();
      throw new Error(
        `Failed to fetch projects with status ${response.status}: ${body}`,
      );
    }

    return response.json() as Promise<Project[]>;
  }
}

export const projectsApi = ApiBlueprint.make({
  name: 'projects',
  params: defineParams =>
    defineParams({
      api: projectsApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
      },
      factory: ({ discoveryApi, fetchApi }) =>
        new ProjectsClient(discoveryApi, fetchApi),
    }),
});
